<script lang="ts" setup>
import { app } from '@/composition/app'
import { Dialog } from '@capacitor/dialog'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { isPwa } from '@ghl/clientportal-core/init'
import {
  AlertTriangleIcon,
  XCloseIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UICheckbox,
  UIDrawer,
  UIDrawerContent,
  UIModal,
  UITextSmMedium,
  UITextSmNormal,
} from '@gohighlevel/ghl-ui'
import { PropType, computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import CommunityButton from '../buttons/CommunityButton.vue'

const iconType = {
  custom: 'icon-custom',
  success: 'icon-success',
  error: 'icon-error',
  warning: 'icon-warning',
  primary: 'icon-primary',
}

const props = defineProps({
  type: {
    type: String as PropType<
      'success' | 'primary' | 'warning' | 'error' | 'custom' | undefined
    >,
    default: 'primary',
  },
  height: {
    type: Number,
    default: 220,
  },
  title: {
    type: String,
  },
  isMessageHtml: {
    type: Boolean,
    default: false,
  },
  message: {
    type: String,
  },
  show: {
    type: Boolean,
  },
  positiveText: {
    type: String,
    default: 'common.confirm',
  },
  icon: {
    type: Function,
    default: AlertTriangleIcon,
  },
  buttonType: {
    type: String as PropType<
      'primary' | 'error' | 'secondary' | 'tertiary' | undefined
    >,
    default: 'primary',
  },
  showActions: {
    type: Boolean,
    default: true,
  },
  checkbox: {
    type: Object as PropType<{
      show: boolean
      checked: boolean
      value: string
      disabled?: boolean
      size?: 'small' | 'medium' | 'large'
    }>,
    default: () => ({
      show: false,
      checked: false,
      value: '',
      disabled: false,
      size: 'medium',
    }),
  },
})

const { t } = useI18n()

const hapticsImpactLight = async () => {
  await Haptics.impact({ style: ImpactStyle.Light })
}

const emit = defineEmits(['update:confirmed', 'update:show', 'update:checkbox'])

const positiveButtonText = computed(() => {
  if (props.positiveText === 'common.confirm') {
    return t('common.confirm')
  }
  // Check if it's a translation key (contains a dot)
  if (props.positiveText?.includes('.')) {
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    return t(props.positiveText)
  }
  return props.positiveText || 'OK'
})

const showModal = ref(false)
const showNativeDialog = async () => {
  const { value } = await Dialog.confirm({
    title: props.title,
    message: props.message || '',
    okButtonTitle: positiveButtonText.value,
  })

  if (value) {
    onPositiveClick()
  } else {
    closeModal()
  }
}

onMounted(() => (showModal.value = props.show))
watch(
  () => props.show,
  (showModalVal: boolean) => {
    if (isPwa() && showModalVal && !props.isMessageHtml) {
      // use haptic feedback
      hapticsImpactLight()
      // Show native dialog for PWA
      showNativeDialog()
      return
    }
    showModal.value = showModalVal
  }
)

watch(
  () => showModal.value,
  (showModalVal: boolean) => {
    emit('update:show', showModalVal)
  }
)

function closeModal() {
  showModal.value = false
  emit('update:show', false)
}

function onPositiveClick() {
  showModal.value = false
  emit('update:confirmed')
}
</script>
<template>
  <UIModal
    v-if="!app.state.isMobile"
    v-model:show="showModal"
    :width="500"
    :z-index="2004"
    class-name="confirmation-modal"
    @update:show="closeModal"
  >
    <div
      class="relative flex flex-col items-start justify-center text-communities-font-primary"
    >
      <XCloseIcon
        id="modal-header"
        class="absolute right-2 top-2 h-5 w-6"
        @click="closeModal"
      />
      <div v-if="icon">
        <div
          class="my-2 h-12 w-12 rounded-full p-1"
          :class="iconType[props.type as string]"
        >
          <component :is="icon" />
        </div>
      </div>
      <div class="title text-lg font-semibold">
        {{ title }}
      </div>
      <div v-if="!props.isMessageHtml">
        {{ message }}
      </div>
      <UITextSmNormal
        v-if="props.isMessageHtml"
        v-sanitized-html="message || ''"
        class="inner-html text-start text-communities-font-primary"
      />
    </div>
    <template #footer>
      <div v-if="checkbox.show">
        <br />
        <UICheckbox
          id="checkbox"
          :checked="checkbox.checked"
          :disabled="checkbox.disabled"
          :size="checkbox.size"
          @update:checked="emit('update:checkbox', { checked: $event })"
        >
          {{ checkbox.value }}
        </UICheckbox>
      </div>
      <div
        v-if="showActions"
        class="mt-4 flex flex-row items-end justify-end gap-3"
      >
        <CommunityButton
          id="negative_btn"
          button-type="tertiary"
          class="px-4"
          @click="closeModal"
          ><UITextSmMedium>
            {{ t('common.cancel') }}
          </UITextSmMedium></CommunityButton
        >
        <CommunityButton
          id="negative_btn"
          :button-type="buttonType"
          class="px-4"
          @click="onPositiveClick"
          ><UITextSmMedium>{{
            positiveButtonText
          }}</UITextSmMedium></CommunityButton
        >
      </div>
    </template>
  </UIModal>
  <UIDrawer
    v-else
    id="post-options-drawer"
    v-model:show="showModal"
    class="rounded-t-xl p-0"
    :placement="'bottom'"
    :height="height"
    :z-index="2006"
    @update:show="closeModal"
  >
    <UIDrawerContent
      id="post-options-drawer-menu"
      v-swipe="{
        direction: 'down',
        callback: () => (showModal = !showModal),
      }"
      class="flex flex-row justify-center px-3"
      :closable="false"
    >
      <div
        class="flex flex-col items-center justify-center text-communities-font-primary"
      >
        <div v-if="icon">
          <div
            class="my-2 h-12 w-12 rounded-full p-1"
            :class="iconType[props.type as string]"
          >
            <component :is="icon" />
          </div>
        </div>
        <div class="title text-lg font-semibold">
          {{ title }}
        </div>
        <div v-if="!props.isMessageHtml" class="text-center">
          {{ message }}
        </div>
        <UITextSmNormal
          v-if="props.isMessageHtml"
          v-sanitized-html="message || ''"
          class="inner-html text-center text-communities-font-primary"
        />
      </div>
      <div
        v-if="showActions"
        class="items-between mt-6 flex w-full flex-row justify-between gap-6"
      >
        <CommunityButton
          id="btn-negative-action"
          button-type="tertiary"
          style="flex: 1 0 auto"
          @click="closeModal"
        >
          {{ t('common.cancel') }}
        </CommunityButton>
        <CommunityButton
          id="btn-positive-action"
          :button-type="buttonType"
          style="flex: 1 0 auto"
          @click="onPositiveClick"
        >
          {{ positiveButtonText }}
        </CommunityButton>
      </div>
    </UIDrawerContent>
  </UIDrawer>
</template>
<style scoped>
.icon-error {
  background: var(--error-100);
  border: 8px solid var(--error-50);
  color: var(--error-400);
}
.icon-warning {
  background: var(--warning-100);
  border: 8px solid var(--warning-50);
  color: var(--warning-400);
}
.icon-primary {
  background: var(--primary-100);
  border: 8px solid var(--primary-50);
  color: var(--primary-400);
}
.icon-success {
  background: var(--success-100);
  border: 8px solid var(--success-50);
  color: var(--success-400);
}

#btn-negative-action,
#btn-positive-action {
  min-width: 74px;
}
:deep(.inner-html li) {
  text-align: left !important;
}
:deep(.inner-html ul) {
  padding-inline-start: 20px !important;
}
</style>
<style>
.confirmation-modal {
  --n-padding-bottom: 0px !important;
}
</style>
